import React, { useEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";

//COMPONENTS
import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import useFormQueryParams from "@/main/hooks/useformQueryParams";

//REDUX
import { AreaButton, KOGAreaButtons } from "@/main/components/shared/styles";
import { KeyValue, KeyValueGeneric } from "@/main/models/common";
import { IntegrationCodeRequest } from "@/main/models/request";
import { getOptionsStatusActiveGeneral } from "@/main/services/data/data";
import { AssessmentTypeEnum } from "@/main/services/enum/AssessmentTypeEnum";
import { IntegrationCodeTypeEnum } from "@/main/services/enum/IntegrationCodeTypeEnum";
import { StatusActiveGeneralEnum } from "@/main/services/enum/StatusActiveGeneralEnum";
import {
	getDefaultOptionDrop,
	getTranslateByKey,
	isNullUndefined,
	mountReducerWithData,
	removeEmptyValues,
} from "@/main/services/utils";
import { useAssessmentTypeReducer } from "@/main/store/assessmentType/assessmentTypeHooks";
import { useBodyshopReducer } from "@/main/store/bodyshop";
import { useCategoryReducer } from "@/main/store/category";
import { useCompanyReducer } from "@/main/store/company";
import { useCompanyTypeReducer } from "@/main/store/companyType";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { useInsurerReducer } from "@/main/store/insurer";
import {
	getIntegrationInterface,
	resetReducerIntegrationInterface,
	useIntegrationTypeReducer,
} from "@/main/store/integrationType";
import { getMappingType, useMappingTypeReducer } from "@/main/store/mappingType";
import { usePositionReducer } from "@/main/store/position";
import { useSubcategoryReducer } from "@/main/store/subcategory";
import { useManufacturerReducer } from "@/main/store/vehicle/manufacturer";
import { useModelReducer } from "@/main/store/vehicle/model";
import { useSubModelReducer } from "@/main/store/vehicle/subModel";
import { useYearReducer } from "@/main/store/vehicle/year";

// STYLES/COMPONENTS LOCAL
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { useAddressReducer } from "@/main/store/address";
import { useClaimFileCategoryReducer } from "@/main/store/claimFileCategory";
import { useInsuranceSublineReducer } from "@/main/store/insuranceSubline";
import { useReasonReducer } from "@/main/store/reason";
import { useSupervisorReducer } from "@/main/store/supervisor";
import { useSupplierReducer } from "@/main/store/supplier";
import { useValuatorReducer } from "@/main/store/valuator";
import { Guid } from "guid-typescript";
import DinamicFiltersMappingCodes, {
	IMERGED_INITIAL_STATE,
	MERGED_INITIAL_STATE,
} from "../dinamicFiltersMappingCodes/dinamicFiltersMappingCodes";
import InterfaceDropdown from "../interfaceDropdown/interfaceDropdown";
import ModalMapCode from "../modalMapCode/modalMapCode";
import * as S from "./styles";

interface IFiltersMappingCodesProps {
	showFilters: boolean;
	formFilters: IntegrationCodeRequest | null;
	setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
	onResetSearch: () => void;
	getDataIntegrationMappingCodes: (filters: IntegrationCodeRequest) => void;
}
export const INITIAL_FORM_STATE_DEFAULT = {
	country: null as KeyValue | null,
	mappingType: null as KeyValueGeneric<IntegrationCodeTypeEnum, string> | null,
	interface: null as KeyValue | null,
	status: null as KeyValue | null,
	nullInterface: false,
};
export type IFormFiltersInitial = typeof INITIAL_FORM_STATE_DEFAULT;

// Notation: This props will be cleaned when the user changes the mapping type
const INITIAL_FORM_STATE = {
	...INITIAL_FORM_STATE_DEFAULT,
	...MERGED_INITIAL_STATE,
};
const KEY_REDUCER = "id-mappingCodes-filters";
export type IFormFiltersMappingCodes = typeof INITIAL_FORM_STATE;

function FiltersMappingCodes({
	showFilters,
	getDataIntegrationMappingCodes,
	onResetSearch,
	setShowFilters,
}: IFiltersMappingCodesProps) {
	// #region Reducers/Options
	const { countriesUser: countryReducer } = useCountryReducer();
	const { mappingType: mappingTypeReducer } = useMappingTypeReducer();
	const { integrationInterface: integrationInterfaceReducer } = useIntegrationTypeReducer();
	const optionsStatus = getOptionsStatusActiveGeneral();
	// Dinamic Fields
	const { isolated: insurersReducer } = useInsurerReducer(KEY_REDUCER);
	const { isolated: bodyshopReducer } = useBodyshopReducer(KEY_REDUCER);
	const { isolated: manufacturersReducer } = useManufacturerReducer(KEY_REDUCER);
	const { isolated: modelsReducer } = useModelReducer(KEY_REDUCER);
	const { isolated: subModelsReducer } = useSubModelReducer(KEY_REDUCER);
	const { isolated: yearsReducer } = useYearReducer(KEY_REDUCER);
	const { isolated: subcategoryReducer } = useSubcategoryReducer(KEY_REDUCER);
	const { isolated: companyReducer } = useCompanyReducer(KEY_REDUCER);
	const { isolated: positionReducer } = usePositionReducer(KEY_REDUCER);
	const { suppliers: supplierReducer } = useSupplierReducer();
	const { category: categoryReducer } = useCategoryReducer();
	const { companyType: companyTypeReducer } = useCompanyTypeReducer();
	const { assessmentType: assessmentTypeReducer } = useAssessmentTypeReducer();
	const { isolated: addressReducer } = useAddressReducer(KEY_REDUCER);
	const { claimFileCategory: claimFileCategoryReducer } = useClaimFileCategoryReducer();
	const { reason: reasonTotalLoss } = useReasonReducer();
	const { isolated: insuranceSublineReducer } = useInsuranceSublineReducer(KEY_REDUCER);
	const { valuators: valuatorReducer } = useValuatorReducer();
	const { domain: supervisorReducer } = useSupervisorReducer();

	// #endregion

	// #region Hooks
	const dispatch = useDispatch();
	const methods = useFormQueryParams<IFormFiltersMappingCodes>({
		defaultValues: INITIAL_FORM_STATE,
		onSubmit: onSubmitFilter,
		mapDefaultValues: {
			country: countryReducer,
			mappingType: mappingTypeReducer,
			interface: integrationInterfaceReducer,
			status: mountReducerWithData(optionsStatus),
			// DinamicFields
			part: { data: [], isLoading: false, isError: false, errorMessages: [] },
			supplier: supplierReducer,
			insurer: insurersReducer,
			bodyshop: bodyshopReducer,
			manufacturer: manufacturersReducer,
			model: modelsReducer,
			subModel: subModelsReducer,
			year: yearsReducer,
			category: categoryReducer,
			subCategory: subcategoryReducer,
			position: positionReducer,
			company: companyReducer,
			companyType: companyTypeReducer,
			assessmentType: assessmentTypeReducer,
			address: addressReducer,
			claimFileCategory: claimFileCategoryReducer,
			reasonTotalLoss: reasonTotalLoss,
			insuranceSubline: insuranceSublineReducer,
			supervisor: supervisorReducer,
			valuator: valuatorReducer,
		},
	});
	const { control, reset, getValues, setValue, customHandleSubmit } = methods;
	//#endregion

	// #region States/Watch
	const optionDefaultCountry = getDefaultOptionDrop(countryReducer.data);
	const optionDefaultStatus = optionsStatus.find((item) => item.key === StatusActiveGeneralEnum.Active)!;
	// #endregion

	//#region Core Functions
	function resetDinamicFields() {
		const values = getValues();
		const keysFormDefault = Object.keys(INITIAL_FORM_STATE_DEFAULT);

		Object.keys(values).forEach((key) => {
			const isFormDefault = keysFormDefault.includes(key);
			if (isFormDefault) return;
			setValue(key as keyof IFormFiltersMappingCodes, MERGED_INITIAL_STATE[key as keyof IMERGED_INITIAL_STATE]);
		});
	}
	//#endregion
	// #region Async Functions
	async function onSubmitFilter(data: IFormFiltersMappingCodes) {
		if (isNullUndefined(data.country) || isNullUndefined(data.mappingType)) return;
		const request: Omit<IntegrationCodeRequest, "pageIndex" | "pageSize"> = {
			guidCountry: String(data.country.key),
			idIntegrationCodeType: data.mappingType?.key,
			active: isNullUndefined(data.status) ? null : data.status?.key == StatusActiveGeneralEnum.Active,
			guidIntegrationInterface: String(data.interface?.key),
			integrationInterfaceNull: data.nullInterface,
			//dinamic fields
			guidBodyshop: String(data.bodyshop?.key),
			code: data.code,
			idAssessmentType: data.assessmentType?.key as AssessmentTypeEnum,
			guidManufacturer: String(data.manufacturer?.key),
			guidModel: String(data.model?.key),
			guidSubModel: String(data.subModel?.key),
			guidYear: String(data.year?.key),
			guidInsurer: String(data.insurer?.key),
			guidCategory: String(data.category?.key),
			guidSubCategory: String(data.subCategory?.key),
			guidPart: String(data.part?.key),
			guidPosition: String(data.position?.key),
			guidCompany: String(data.company?.key),
			guidSupplier: String(data.supplier?.key),
			guidValuator: String(data.valuator?.key),
			guidSupervisor: String(data.supervisor?.key),
			idCompanyType: data.companyType?.key as CompanyTypeEnum,
			guidAddress: String(data.address?.key),
			idClaimFileCategory: data.claimFileCategory?.key ? Number(data.claimFileCategory?.key) : undefined,
			guidReasonTotalLoss: String(data.reasonTotalLoss?.key),
			guidInsuranceSubline: String(data.insuranceSubline?.key),
		};
		getDataIntegrationMappingCodes(removeEmptyValues(request));
		setShowFilters(false);
	}
	// #endregion

	// #region Handle Functions
	function handleClear() {
		reset(INITIAL_FORM_STATE);
		onResetSearch();
		setValue("country", optionDefaultCountry);
		setValue("status", optionDefaultStatus);
		dispatch(resetReducerIntegrationInterface());
		dispatch(
			getIntegrationInterface({
				idIntegrationType: 0,
				guidIntegration: Guid.EMPTY,
			}),
		);
	}
	// #endregion

	// #region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
		if (isNullUndefined(mappingTypeReducer.data)) dispatch(getMappingType());
		if (isNullUndefined(integrationInterfaceReducer.data))
			dispatch(
				getIntegrationInterface({
					idIntegrationType: 0,
					guidIntegration: Guid.EMPTY,
				}),
			);
	}, []);
	// #endregion

	const disabledCountry = isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;
	const disabledMappingType = isNullUndefined(mappingTypeReducer.data) || mappingTypeReducer?.data?.length === 1;

	return (
		<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
			<FormProvider {...(methods as any)}>
				<form onSubmit={customHandleSubmit} noValidate>
					<S.Container>
						<S.RowInputs>
							<KogAutoCompleteControlled
								id="id-mappingCodes-select-country"
								nameCustom="country"
								label={getTranslateByKey("information.country")}
								isDefaultValueUnique
								valueDefaultKey={optionDefaultCountry?.key}
								control={control}
								rulesCustom={{ required: true }}
								setValueCustom={setValue}
								options={countryReducer.data}
								isLoading={countryReducer.isLoading}
								disabled={disabledCountry}
								onChangeCustom={() => {}}
							/>
							<KogAutoCompleteControlled
								id="id-mappingCodes-select-mappingType"
								nameCustom="mappingType"
								label={getTranslateByKey("information.mappingType")}
								isDefaultValueUnique
								control={control}
								setValueCustom={setValue}
								options={mappingTypeReducer.data}
								isLoading={mappingTypeReducer.isLoading}
								rulesCustom={{ required: true }}
								disabled={disabledMappingType}
								onChangeCustom={() => {
									resetDinamicFields();
								}}
							/>
							<InterfaceDropdown />
							<DinamicFiltersMappingCodes
								keyReducer={KEY_REDUCER}
								INITIAL_FORM_STATE_DEFAULT={INITIAL_FORM_STATE_DEFAULT}
								fieldsRequired={[]}
							/>
							<KogAutoCompleteControlled
								id="id-mappingCodes-select-status"
								nameCustom="status"
								label={getTranslateByKey("information.status")}
								valueDefaultKey={optionDefaultStatus?.key}
								control={control}
								rulesCustom={{ required: false }}
								setValueCustom={setValue}
								options={optionsStatus}
								onChangeCustom={() => {}}
							/>
						</S.RowInputs>
						<KOGAreaButtons>
							<SecondaryButton id="id-mappingCodes-btn-submit" type="submit">
								{getTranslateByKey("information.search")}
							</SecondaryButton>
							<SecondaryButton id="id-mappingCodes-btn-clear" type="button" onClick={handleClear}>
								{getTranslateByKey("information.clear")}
							</SecondaryButton>
						</KOGAreaButtons>
					</S.Container>
				</form>
			</FormProvider>
		</Box>
	);
}

function FiltersMappingCodesWrapper(props: Omit<IFiltersMappingCodesProps, "showFilters" | "setShowFilters">) {
	//#region States
	const [isOpenModalMapCode, setIsOpenModalMapCode] = useState<boolean>(false);
	const [showFilters, setShowFilters] = useState<boolean>(true);
	//#endregion

	//#region HandleFunctions
	function handleFilters() {
		setShowFilters((prev) => !prev);
		window.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	}
	function handleNewMapCode(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.preventDefault();
		e.stopPropagation();
		setIsOpenModalMapCode(true);
	}
	function refreshSearchGrid(idMappingTypeModal: string) {
		const currentFilters = props.formFilters;
		const isSameMappingType = currentFilters?.idIntegrationCodeType === idMappingTypeModal;

		if (isSameMappingType) {
			props.getDataIntegrationMappingCodes(currentFilters as IntegrationCodeRequest);
		}
	}
	//#endregion

	const mergedProps: IFiltersMappingCodesProps = {
		...props,
		showFilters,
		setShowFilters,
	};
	return (
		<>
			<AreaButton>
				<Button onClick={handleFilters} id="id-mappingCodes-btn-filters">
					{getTranslateByKey("information.filters")}
				</Button>
				<Button onClick={handleNewMapCode} id="id-mappingCodes-btn-new">
					{getTranslateByKey("information.newMapCode")}
				</Button>
			</AreaButton>
			<FiltersMappingCodes {...mergedProps} />

			<ModalMapCode
				isOpenModal={isOpenModalMapCode}
				closeModal={() => setIsOpenModalMapCode(false)}
				setIsOpenModal={setIsOpenModalMapCode}
				refreshSearchGrid={refreshSearchGrid}
			/>
		</>
	);
}
export default FiltersMappingCodesWrapper;
